import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store/index.js";
import Popup from "@/gts-popup.js";
import i18n from "@/fmt/i18n.js";
import firebaseMessaging from "@/firebase.js";
import { generateUuid } from "@/utils.js";

import { useFavicon, useTitle } from "@vueuse/core";
const icon = useFavicon();
const title = useTitle();

// import { versionChecker } from "@/helper/versionChecker.js";

import { TokenService, globalService, agencyService, userService } from "@/service/index.js";

import TestDev from "@/pages/Test.vue";
import PackageCompTest from "@/pages/PackageCompTest.vue";

import Dashboard from "@/pages/Dashboard.vue";
// import Dashboard2 from "@/pages/Dashboard2.vue";

import News from "@/pages/News.vue";
// import PageNotFound from "@/pages/PageNotFound.vue";

import Hotel from "@/pages/hotel/HotelSearch.vue";
import HotelCache from "@/pages/hotel/HotelCacheSearch.vue";
import HotelCheckout from "@/pages/hotel/HotelCheckout.vue";

import Flight from "@/pages/flight/FlightSearch.vue";
// import FlightOrderDetail from "@/pages/flight/FlightOrderDetail.vue";
import FlightCheckout from "@/pages/flight/FlightCheckout.vue";

import RentACar from "@/pages/rentacar/RentACarSearch.vue";
import RentACarCheckout from "@/pages/rentacar/RentACarCheckout.vue";

import Transfer from "@/pages/transfer/TransferSearch";
import TransferCheckout from "@/pages/transfer/TransferCheckout.vue";

import OrderSearch from "@/pages/order/OrderSearch.vue";
import OrderDetail from "@/pages/order/OrderDetail.vue";

import FlightSalesReport from "@/pages/report/FlightSalesReport.vue";
import HotelSalesReport from "@/pages/report/HotelSalesReport.vue";
import TransferSalesReport from "@/pages/report/TransferSalesReport.vue";
import RentACarSalesReport from "@/pages/report/RentACarSalesReport.vue";

import TaskList from "@/pages/task/TaskList.vue";

import InvoiceList from "@/pages/finance/InvoiceList.vue";
import BalanceUpdate from "@/pages/finance/BalanceUpdate.vue";
import AccTransactionList from "@/pages/finance/AccTransactionList.vue";

import Service from "@/pages/service/Service.vue";

import Notification from "@/pages/NotificationPage.vue";

import Version from "@/pages/Version.vue";
import Loading from "@/pages/Loading.vue";

import StyleGuide from "@/dev/StyleGuide.vue";

import Login from "@/pages/user/Login.vue";
import Register from "@/pages/user/Register.vue";
import TermsAndConditions from "@/pages/user/TermsAndConditions.vue";
import PrivacyPolicy from "@/pages/user/PrivacyPolicy.vue";
import Settings from "@/pages/user/settings/Settings.vue";

import ResetPassword from "@/pages/user/ResetPassword.vue";

import PackageSearch from "@/pages/PackageSearch.vue";

Vue.use(VueRouter);

const router = new VueRouter({
   mode: "history",
   base: process.env.BASE_URL,
   routes: [
      {
         path: "/",
         name: "Dashboard",
         component: Dashboard,
         meta: {
            title: "Dashboard",
         },
      },
      {
         path: "/guide",
         name: "StyleGuide",
         component: StyleGuide,
         meta: {
            title: "StyleGuide",
         },
      },
      {
         path: "/testdev",
         name: "TestDev",
         component: TestDev,
         meta: {
            title: "TestDev",
         },
      },
      {
         path: "/package",
         name: "Package",
         component: PackageCompTest,
         meta: {
            title: "PackageCompTest",
         },
      },

      {
         path: "/notifications",
         name: "Notification",
         component: Notification,
         meta: {
            title: "Notification",
            roles: [19],
         },
      },
      {
         path: "/hotel",
         name: "HotelSale",
         component: Hotel,
         meta: {
            title: "HotelSale",
            roles: [2],
            agencyRequired: true,
         },
      },
      {
         path: "/hotelcache",
         name: "HotelCache",
         component: HotelCache,
         meta: {
            title: "HotelCache",
            roles: [2],
            agencyRequired: true,
         },
      },
      {
         path: "/loading",
         name: "Loading",
         component: Loading,
      },
      {
         path: "/hotel/checkout/:uuid/:basketuuid",
         name: "HotelCheckout",
         component: HotelCheckout,
         meta: {
            title: "HotelCheckout",
            roles: [5],
            agencyRequired: true,
         },
      },
      {
         path: "/flight",
         name: "FlightSale",
         component: Flight,
         meta: {
            title: "FlightSale",
            roles: [1],
            agencyRequired: true,
         },
      },
      {
         path: "/rentacar",
         name: "RentACarSale",
         component: RentACar,
         meta: {
            title: "RentACar",
            roles: [22],
            agencyRequired: true,
         },
      },
      {
         path: "/packages",
         name: "PackageSearch",
         component: PackageSearch,
         meta: {
            title: "PackageSearch",
            roles: [25],
            agencyRequired: true,
         },
      },
      {
         path: "/rentacar/checkout/:searchUuid",
         name: "RentACarCheckout",
         component: RentACarCheckout,
         meta: {
            title: "RentACarCheckut",
            roles: [22],
            agencyRequired: true,
         },
      },

      {
         path: "/transfer",
         name: "TransferSale",
         component: Transfer,
         meta: {
            title: "TransferSale",
            roles: [3],
            agencyRequired: true,
         },
      },
      {
         path: "/transfer/checkout/:uuid",
         name: "TransferCheckout",
         component: TransferCheckout,
         meta: {
            title: "TransferCheckout",
            roles: [6],
            agencyRequired: true,
         },
      },
      {
         path: "/flight/checkout/:uuid",
         name: "FlightCheckout",
         component: FlightCheckout,
         meta: {
            title: "FlightCheckout",
            roles: [4],
            agencyRequired: true,
         },
      },
      {
         path: "/sale/order",
         name: "OrderSearch",
         component: OrderSearch,
         meta: {
            title: "OrderSearch",
            roles: [7],
         },
      },
      {
         path: "/order/detail/:orderUuid",
         name: "OrderDetail",
         component: OrderDetail,
         meta: {
            title: "OrderDetail",
            roles: [8],
         },
      },
      {
         path: "/report/flightSalesReport",
         name: "FlightSalesReport",
         component: FlightSalesReport,
         meta: {
            title: "FlightSalesReport",
            roles: [10],
         },
      },
      {
         path: "/report/hotelSalesReport",
         name: "HotelSalesReport",
         component: HotelSalesReport,
         meta: {
            title: "HotelSalesReport",
            roles: [11],
         },
      },
      {
         path: "/report/transferSalesReport",
         name: "TransferSalesReport",
         component: TransferSalesReport,
         meta: {
            title: "TransferSalesReport",
            roles: [21],
         },
      },
      {
         path: "/report/rentACarSalesReport",
         name: "RentACarSalesReport",
         component: RentACarSalesReport,
         meta: {
            title: "RentACarSalesReport",
            roles: [23],
         },
      },
      {
         path: "/task/taskList",
         name: "TaskList",
         component: TaskList,
         meta: {
            title: "TaskList",
            roles: [9],
         },
      },
      {
         path: "/finance/invoiceList",
         name: "InvoiceList",
         component: InvoiceList,
         meta: {
            title: "InvoiceList",
            roles: [12],
            agencyRequired: true,
         },
      },
      {
         path: "/finance/accTransactionList",
         name: "AccTransactionList",
         component: AccTransactionList,
         meta: {
            title: "AccTransactionList",
            roles: [13],
            agencyRequired: true,
         },
      },
      {
         path: "/finance/balanceUpdate",
         name: "BalanceUpdate",
         component: BalanceUpdate,
         meta: {
            title: "BalanceUpdate",
            roles: [14],
            agencyRequired: true,
         },
      },
      {
         path: "/service",
         name: "Service",
         component: Service,
         meta: {
            title: "Service",
         },
      },
      {
         path: "/announcement",
         name: "Announcement",
         component: News,
         meta: {
            title: "Announcement",
         },
      },
      {
         path: "/login",
         name: "Login",
         component: Login,
         meta: {
            title: "Login",
         },
      },
      {
         path: "/ResetPassword",
         name: "ResetPassword",
         component: ResetPassword,
         props: (route) => ({ query: route.query.token }),
      },

      {
         path: "/terms-and-conditions",
         name: "TermsAndConditions",
         component: TermsAndConditions,
         meta: {
            title: "TermsAndConditions",
         },
      },
      {
         path: "/privacy-policy",
         name: "PrivacyPolicy",
         component: PrivacyPolicy,
         meta: {
            title: "PrivacyPolicy",
         },
      },
      {
         path: "/register",
         name: "RegisterPage",
         component: Register,
         meta: {
            title: "Register",
         },
      },
      {
         path: "/settings/",
         name: "SettingsPage",
         component: Settings,
         meta: {
            title: "Settings",
            roles: [15, 16, 17, 18],
         },
      },
      {
         path: "/version",
         name: "Version",
         component: Version,
         meta: {
            title: "Version",
            roles: [20],
         },
      },

      {
         path: "*",
         name: "Dashboard",
         component: Dashboard,
         meta: {
            title: "Dashboard",
         },
      },
   ],
});

router.beforeEach((to, from, next) => {
   const bodyClass = document.body.classList.contains("g-form-invalid");
   bodyClass ? document.body.classList.remove("g-form-invalid") : undefined;

   store.commit("app/setIsProgressGifActive", false);
   store.state.app.progress.message = "";

   if (from === to) {
      return;
   }

   if (to.name == "Announcement") return next();

   const nearestWithTitle = to.matched
      .slice()
      .reverse()
      .find((r) => r.meta && r.meta.title);
   const nearestWithMeta = to.matched
      .slice()
      .reverse()
      .find((r) => r.meta && r.meta.metaTags);
   const previousNearestWithMeta = from.matched
      .slice()
      .reverse()
      .find((r) => r.meta && r.meta.metaTags);

   // if (to.path == "/login" || to.path == "/ResetPassword")
   if (to.path == "/ResetPassword") {
      TokenService.delJwtInfo();
      return next();
   }

   const publicPages = ["/login", "/register", "/ResetPassword", "/announcement"];
   const authRequired = !publicPages.includes(to.path);
   var loggedIn = !TokenService.isJwtExpired();
   var loggedOut = TokenService.isJwtDeleted();

   if (to.path == "/logout" || loggedOut) {
      TokenService.delJwtInfo();
      localStorage.clear();
      sessionStorage.clear();

      if (to.path == "/login") {
         return next();
      }

      var currentPath = window.location.pathname;
      if (currentPath == "/login") return next();
      else router.push({ path: "/login", query: { returnUrl: currentPath } });
      return new Promise(() => {
         return false;
      });
   }

   const toRoles = to.meta.roles;
   const fromRoles = from.meta.roles;
   var userRoles = null;
   if (localStorage.getItem("auths")) {
      try {
         userRoles = JSON.parse(localStorage.getItem("auths"));
      } catch {
         // console.warn("Cannot parse auths :", localStorage.getItem("auths"));
      }
   }

   if (loggedIn && to.path == "/login") {
      return next({ path: "/" });
   }

   // if (loggedIn && to.path == "/" && userRoles != null && userRoles.includes(25)) {
   //    return next({ path: "/v2" });
   // }

   // if (!versionChecker.checkVersion()) {
   //    versionChecker.reloadApp();
   // }

   if (authRequired && !loggedIn) {
      const refreshTokenRequestModel = {
         jwt: TokenService.getJwtToken(),
         refreshToken: TokenService.getRefreshToken(),
      };

      userService.refreshToken(refreshTokenRequestModel).then(function (response) {
         TokenService.setJwtInfo(response.jwt, response.jwtDuration, response.jwtRefreshToken);
      });

      loggedIn = true;

      // return next({ path: "/login", query: { returnUrl: to.fullPath } });
   }

   if (toRoles) {
      if (!loggedIn) {
         // not logged in so redirect to login page with the return url
         if (from.name == "Login") {
            return;
         }
         TokenService.delJwtInfo();
         return next({ path: "/login", query: { returnUrl: to.path } });
      }
      // check if route is restricted by role
      else if (toRoles.length && userRoles != null && !toRoles.some((role) => userRoles.includes(role))) {
         Popup.warning(`${i18n.t("router_noAuth")}`, `${i18n.t("router_contactForAuth")}`, "", 30000);

         if (fromRoles && !fromRoles.some((role) => userRoles.includes(role))) {
            //PAGE FROM IS NOT AUTHORIZED AS WELL, SO REDIRECT TO HOME PAGE
            return next({ path: "/" });
         } else if (from.path == "/") {
            //PAGE FROM IS EITHER DASHBOARD OR NONE(ACCESSED BY URL)
            return next({ path: "/" });
         } else {
            //PAGE FROM IS AUTHORIZED SO STAY ON THE PAGE
            return;
         }
      }
   }
   if (authRequired && store.state.user.user && !store.state.user.user.initiated) {
      globalService.startupService(false, {}).then((response) => {
         Vue.prototype.$constants.showTargetAchievementIcon = response.info.originalAgency.displayAgencyKpi;
         response.info.originalGsa.cssCode && (Vue.prototype.$constants.brand = response.info.originalGsa);

         icon.value = `favicons/${Vue.prototype.$constants.brand.cssCode}.png`;
         title.value = Vue.prototype.$constants.brand.title;

         if (response.result.success) {
            store.commit("app/setSpaLastVersion", response.spaVersion);
            // if (!versionChecker.checkVersion()) {
            //    versionChecker.reloadApp();
            // }

            agencyService.listGsas({}).then((listGsasResponse) => {
               if (listGsasResponse.result.success) {
                  store.commit("app/setGsaList", listGsasResponse.gsas);
               }
            });
            agencyService.listAgencies({}).then((listAgenciesResponse) => {
               if (listAgenciesResponse.result.success) {
                  store.commit("app/setAgencyList", listAgenciesResponse.agencies);
               }
            });
            const userInfos = {
               //User
               language: response.info.user.language,
               name: response.info.user.name,
               surname: response.info.user.surname,
               timezoneId: response.info.user.timezoneId,
               email: response.info.user.email,
               id: response.info.user.id,
               agencyOnBehalfOfId: response.info.onBehalfOfAgency.id,
               gsaOnBehalfOfId: response.info.onBehalfOfGsa.id,
               //OriginalAgency
               agencyName: response.info.originalAgency.name,
               agencyIatiId: response.info.originalAgency.iatiId,
               agencyId: response.info.originalAgency.id,

               //OriginalGSA
               gsaId: response.info.originalGsa.id,
               gsaName: response.info.originalGsa.name,

               initiated: true,
               originalGsaCssCode: response.originalGsaCssCode,
            };

            store.commit("user/updateUserInformations", userInfos);
            switch (response.userLanguage) {
               case 1:
                  i18n.locale = "tr";
                  break;
               case 2:
                  i18n.locale = "en";
                  break;
               case 3:
                  i18n.locale = "de";
                  break;
               default:
                  i18n.locale = "en";
                  break;
            }
            if (to.meta.agencyRequired && response.originalAgencyId == -1 && response.onBehalfOfAgencyId == -1) {
               if (from && (publicPages.includes(from.path) || from.path == "/" || from.meta.agencyRequired)) {
                  if (!publicPages.includes(from.path)) {
                     setTimeout(function () {
                        Popup.warning(`${i18n.t("router_chooseAgency")}`, `${i18n.t("router_agencyRequired")}`, "", 30000);
                     }, 500);
                  }
                  //PAGE FROM IS EITHER DASHBOARD OR NONE(ACCESSED BY URL) OR FROM PAGE IS ONLY AUTHORIZED FOR AGENCYUSERS AS WELL
                  return next({ path: "/" });
               } else {
                  Popup.warning(`${i18n.t("router_chooseAgency")}`, `${i18n.t("router_agencyRequired")}`, "", 30000);
                  return;
               }
            }
            next();
         }
      });
      firebaseMessaging
         .getToken({ vapidKey: process.env.VUE_APP_FIREBASE_VAPID_KEY })
         .then((currentToken) => {
            if (currentToken) {
               if (loggedIn) {
                  var deviceInfo = getDeviceInfo();
                  let query = {
                     uuid: generateUuid(),
                     application: 2,
                     token: currentToken,
                     os: deviceInfo.os,
                     osVersion: deviceInfo.osVersion.toString(),
                     browser: deviceInfo.browser,
                     browserVersion: deviceInfo.browserVersion.toString(),
                  };
                  globalService.notificationRegister(query).then((res) => {
                     if (res.result.success) {
                        store.commit("app/setNotificationCount", res.unreadCount);
                     }
                  });
               }
            }
         })
         .catch((err) => {
            console.log("error", err);
         });
   } else {
      var userAgencyId = store.state.user.user.agencyId;
      var userAgencyOnBehalfOfId = store.state.user.user.onBehalfOfs.agencyOnBehalfOfId;

      if (to.meta.agencyRequired && userAgencyId == -1 && userAgencyOnBehalfOfId == -1) {
         if (from && (publicPages.includes(from.path) || from.path == "/" || from.meta.agencyRequired)) {
            if (!publicPages.includes(from.path)) {
               setTimeout(function () {
                  Popup.warning(`${i18n.t("router_chooseAgency")}`, `${i18n.t("router_agencyRequired")}`, "", 30000);
               }, 500);
            }
            //PAGE FROM IS EITHER DASHBOARD OR NONE(ACCESSED BY URL) OR FROM PAGE IS ONLY AUTHORIZED FOR AGENCYUSERS AS WELL
            return next({ path: "/" });
         } else {
            Popup.warning(`${i18n.t("router_chooseAgency")}`, `${i18n.t("router_agencyRequired")}`, "", 30000);
            return;
         }
      }
      next();
   }

   if (nearestWithTitle) {
      setBrowserTitle(nearestWithTitle.meta.title);
   } else if (previousNearestWithMeta) {
      setBrowserTitle(previousNearestWithMeta.meta.title);
   }

   // Array.from(document.querySelectorAll("[data-vue-router-controlled]")).map((el) => el.parentNode.removeChild(el));

   if (!nearestWithMeta) return;

   // nearestWithMeta.meta.metaTags
   //     .map((tagDef) => {
   //         const tag = document.createElement("meta");

   //         Object.keys(tagDef).forEach((key) => {
   //             tag.setAttribute(key, tagDef[key]);
   //         });

   //         tag.setAttribute("data-vue-router-controlled", "");

   //         return tag;
   //     })
   //     .forEach((tag) => document.head.appendChild(tag));

   next();
});

function setBrowserTitle(title) {
   document.title = i18n.t("router_title" + title);
}

function getDeviceInfo() {
   "use strict";

   var module = {
      options: [],
      header: [navigator.platform, navigator.userAgent, navigator.appVersion, navigator.vendor, window.opera],
      dataos: [
         { name: "Windows Phone", value: "Windows Phone", version: "OS" },
         { name: "Windows", value: "Win", version: "NT" },
         { name: "iPhone", value: "iPhone", version: "OS" },
         { name: "iPad", value: "iPad", version: "OS" },
         { name: "Kindle", value: "Silk", version: "Silk" },
         { name: "Android", value: "Android", version: "Android" },
         { name: "PlayBook", value: "PlayBook", version: "OS" },
         { name: "BlackBerry", value: "BlackBerry", version: "/" },
         { name: "Macintosh", value: "Mac", version: "OS X" },
         { name: "Linux", value: "Linux", version: "rv" },
         { name: "Palm", value: "Palm", version: "PalmOS" },
      ],
      databrowser: [
         { name: "Chrome", value: "Chrome", version: "Chrome" },
         { name: "Firefox", value: "Firefox", version: "Firefox" },
         { name: "Safari", value: "Safari", version: "Version" },
         { name: "Internet Explorer", value: "MSIE", version: "MSIE" },
         { name: "Edge", value: "Edg", version: "Edg" },
         { name: "Opera", value: "Opera", version: "Opera" },
         { name: "Opera", value: "OPR", version: "OPR" },
         { name: "BlackBerry", value: "CLDC", version: "CLDC" },
         { name: "Mozilla", value: "Mozilla", version: "Mozilla" },
      ],
      init: function () {
         var agent = this.header.join(" "),
            os = this.matchItem(agent, this.dataos),
            browser = this.matchItem(agent, this.databrowser);

         return { os: os, browser: browser };
      },
      matchItem: function (string, data) {
         var i = 0,
            j = 0,
            regex,
            regexv,
            match,
            matches,
            version;

         for (i = 0; i < data.length; i += 1) {
            regex = new RegExp(data[i].value, "i");
            match = regex.test(string);
            if (match) {
               regexv = new RegExp(data[i].version + "[- /:;]([\\d._]+)", "i");
               matches = string.match(regexv);
               version = "";
               if (matches) {
                  if (matches[1]) {
                     matches = matches[1];
                  }
               }
               if (matches) {
                  matches = matches.split(/[._]+/);
                  for (j = 0; j < matches.length; j += 1) {
                     if (j === 0) {
                        version += matches[j] + ".";
                     } else {
                        version += matches[j];
                     }
                  }
               } else {
                  version = "0";
               }
               return {
                  name: data[i].name,
                  version: parseFloat(version),
               };
            }
         }
         return { name: "unknown", version: 0 };
      },
   };

   var e = module.init(),
      debug;

   debug = {
      os: e.os.name,
      osVersion: e.os.version,
      browser: e.browser.name,
      browserVersion: e.browser.version,
   };

   return debug;
}

export default router;
